import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router';
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  Tooltip,
  useTheme,
  Chip,
  CardHeader,
  Divider,
  IconButton,
  Hidden,
  Stack,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField, Select, MenuItem, FormControl, InputLabel,
  Modal
} from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import NotificationsIcon from '@mui/icons-material/Notifications';
import HotelIcon from '@mui/icons-material/Hotel';
import NumbersIcon from '@mui/icons-material/Numbers';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import CancelIcon from '@mui/icons-material/Cancel';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import DescriptionIcon from '@mui/icons-material/Description';
import moment from 'moment';
import { showAlert as showGlobalAlert } from '../store/actions/alertActions';
import { RootState } from '../store/store';
import MobileDataList from '../components/MobileDataList';
import MobileShiftCard from '../components/shift/MobileShiftCard';
import useStatesFilter from '../components/StatesFilterDropdown';
import Table from '../components/Table';
import { refreshDataWithDateRange, searchByTextDashboard } from '../store/actions/shiftActions';
import FormattedShift from '../models/FormattedShift';
import { formatDateToCalendarTime, formatDateToLongDate, formatDateToOnlyDate, formatDateToOnlyTime, formatErrorMessage } from '../utils/common';
import GlobalSearch from '../components/GlobalSearch';
import FleetIoFormsService from '../services/FleetIoFormsService';
import ShiftsService from '../services/ShiftsService';
import { hide, show } from '../store/actions/busyIndicatorActions';
import {
  getCheckinStatus,
  getLocationStatus,
  getPrestartStatus,
  getBreakStatus,
  getIconContainerStyle,
  getIconColor,
  iconStyle
} from '../utils/complianceUtils';
import FleetIoService from '../services/FleetIoService';
import LocalStorageService from '../services/LocalStorageService';

interface Props {
  visibleRows: Array<FormattedShift>;
  fetchAllRows: any;
  companyId: number | undefined;
  searchedText: string,
  filterByText: any,
  showBusyIndicator: () => any,
  hideBusyIndicator: () => any,
  showAlert: (message: any) => void
}

const DashboardToday: React.FC<Props> = ({ visibleRows, fetchAllRows, searchedText, filterByText, showBusyIndicator, hideBusyIndicator, showAlert }) => {
  const [page, setPage] = useState(0);
  const [sortModel, setSortModel] = useState([]);
  const [fleetIoPrecheckCompleteShifts, setFleetIoPrecheckCompleteShifts] = useState<Array<{ shiftId: number; submissionId: number; state: number }>>([]);
  const [fleetIoFridgeInspectionCompleteShifts, setFleetIoFridgeInspectionCompleteShifts] = useState<Array<{ shiftId: number; submissionId: number; state: number }>>([]);
  const [isFleetIoLoading, setIsFleetIoLoading] = useState(true);
  const [notifyDialogOpen, setNotifyDialogOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [formSubmissionData, setFormSubmissionData] = useState<any>(null);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [selectedSummary, setSelectedSummary] = useState<string>('');
  const [rowsPerPage, setRowsPerPage] = useState(30);
  const { StatesDropdown, selectedStates } = useStatesFilter();

  const [open, setOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState('');

  const handleOpenImage = (imageSrc: string) => {
    if (imageSrc === '') return;
    setSelectedImage(imageSrc);
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  useEffect(() => {
    console.log(visibleRows);
    if (visibleRows?.length > 0) {
      setIsFleetIoLoading(true);
      FleetIoFormsService.matchSubmissionsWithVehicles(visibleRows)
        .then((response) => {
          console.log('Matched submissions:', response);
          setFleetIoPrecheckCompleteShifts(response.precheckCompleteShifts);
          setFleetIoFridgeInspectionCompleteShifts(response.fridgeInspectionCompleteShifts);
        })
        .catch((error) => {
          console.error('Error matching submissions with vehicles:', error);
        })
        .finally(() => {
          setIsFleetIoLoading(false);
        });
    }
  }, [visibleRows]);

  // Filter visibleRows and visibleNrRows by selected states
  const filteredVisibleRows: FormattedShift[] = selectedStates && selectedStates.length > 0
    ? visibleRows.filter((x) => {
      const id = x?.jobsite?.address?.state?.id;
      return typeof id === 'number' && selectedStates.includes(id);
    })
    : visibleRows;

  useEffect(() => {
    const start = moment().toDate().toDateString();
    const end = moment().toDate().toDateString();
    fetchAllRows(start, end);
  }, []);

  const handleNotifyClick = (row: any) => {
    setSelectedRow(row);
    // Pre-fill message with relevant info
    const message = getBreakStatus(row.startedOnActualUTC, row.shiftBreaks, row.endedOnActualUTC).tooltip;
    setNotificationMessage(message);
    setNotifyDialogOpen(true);
  };

  const formatMsToTime = (ms: number) => {
    if (!ms) return '--';

    const totalMinutes = Math.floor(ms / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
  };

  const breachColumns = [
    {
      field: 'urgency',
      headerName: 'Message',
      width: 200,
      renderCell: ({ row }: any) => {
        const { tooltip, severity } = getBreakStatus(
          row.startedOnActualUTC,
          row.shiftBreaks,
          row.endedOnActualUTC
        );

        const severityStyles: { [key: string]: { bg: string; text: string } } = {
          high: { bg: '#f8d7da', text: '#721c24' },
          warning: { bg: '#fff3cd', text: '#856404' },
          '': { bg: '#d4edda', text: '#155724' },
        };

        const style = severityStyles[severity] || severityStyles[''];

        return (
          <Tooltip title={tooltip}>
            <span>
              <Chip
                label={tooltip}
                sx={{
                  backgroundColor: style.bg,
                  color: style.text,
                  borderRadius: '12px',
                  padding: '0.5rem',
                  fontWeight: 500,
                  height: 'auto',
                  fontSize: '10px',
                  '& .MuiChip-label': {
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    textAlign: 'left',
                    padding: 0
                  }
                }}
              />
            </span>
          </Tooltip>
        );
      },
    },
    {
      field: 'driver',
      headerName: 'Driver',
      width: 150,
      renderCell: ({ row }: any) => (
        <Tooltip title={row.userEmail || ''}>
          <span>{`${row.user.firstName} ${row.user.lastName}`}</span>
        </Tooltip>
      )
    },
    {
      field: 'userPhone',
      headerName: 'Mobile',
      flex: 1,
      minWidth: 100,
      filterable: false,
      hide: false,
      renderHeader: () => (
        <Tooltip title="Mobile number of the assigned driver">
          <span style={{ fontWeight: '600' }}>Mobile</span>
        </Tooltip>
      ),
      renderCell: (params: any) => {
        const { value } = params;
        return (
          <Tooltip title={value || ''}>
            <span>{value}</span>
          </Tooltip>
        );
      },
    },
    {
      field: 'scheduledStartDateUTC',
      headerName: 'Date',
      flex: 1,
      minWidth: 100,
      type: 'date',
      filterable: false,
      renderHeader: () => (
        <Tooltip title="Scheduled date of the shift">
          <span style={{ fontWeight: '600' }}>Date</span>
        </Tooltip>
      ),
      renderCell: (params: any) => {
        const { value, row } = params;
        return (
          <Tooltip title={formatDateToCalendarTime(row.scheduledStartUTC, true) || ''}>
            <span>
              {formatDateToOnlyDate(row.scheduledStartUTC, true)}
            </span>
          </Tooltip>
        );
      }
    },
    {
      field: 'amOrPm',
      headerName: 'Shift',
      minWidth: 10,
      filterable: true,
      renderCell: (params: any) => {
        const { value, row } = params;
        return (
          <Tooltip title={value}>
            <span>{value || '-'}</span>
          </Tooltip>
        );
      }
    },
    {
      field: 'startedOnUTC',
      headerName: 'Actual Start',
      flex: 1,
      minWidth: 100,
      type: 'date',
      filterable: false,
      renderHeader: () => (
        <Tooltip title="Actual time when the shift started">
          <span style={{ fontWeight: '600' }}>Actual Start</span>
        </Tooltip>
      ),
      renderCell: (params: any) => {
        const { value } = params;
        return (
          <Tooltip title={formatDateToCalendarTime(value, true) || ''}>
            <span>
              {formatDateToOnlyTime(value, true)}
            </span>
          </Tooltip>
        );
      }
    },
    {
      field: 'endedOnUTC',
      headerName: 'Actual End',
      flex: 1,
      minWidth: 100,
      type: 'date',
      filterable: false,
      renderHeader: () => (
        <Tooltip title="Actual time when the shift ended">
          <span style={{ fontWeight: '600' }}>Actual End</span>
        </Tooltip>
      ),
      renderCell: (params: any) => {
        const { value } = params;
        return (
          <Tooltip title={formatDateToCalendarTime(value, true) || ''}>
            <span>
              {value && formatDateToOnlyTime(value, true)}
            </span>
          </Tooltip>
        );
      }
    },
    {
      field: 'totalTimeWorked',
      headerName: 'Total Time Worked',
      width: 150,
      renderCell: ({ value }: any) => <span>{formatMsToTime(value)}</span>
    },
    {
      field: 'totalBreakTime',
      headerName: 'Total Break Time',
      width: 150,
      renderCell: ({ value }: any) => <span>{formatMsToTime(value)}</span>
    },
    {
      field: 'totalDriving',
      headerName: 'Total Driving',
      width: 180,
      renderCell: ({ row }: any) => (
        <span>{row.scheduledKms} km</span>
      )
    },
    {
      field: 'action',
      headerName: 'Warn Driver',
      width: 180,
      renderCell: ({ row }: any) => (
        <Tooltip title="Click to warn" arrow>
          <IconButton
            color="primary"
            aria-label="notify"
            onClick={() => handleNotifyClick(row)}
          >
            <NotificationsIcon />
          </IconButton>
        </Tooltip>
      )
    }
  ];

  const renderEachShiftCard = (row: any) => {
    let shiftType: 'ongoing' | 'completed' | 'scheduled' = 'scheduled';
    if (row.shiftStatus === 'Ongoing') {
      shiftType = 'ongoing';
    } else if (row.shiftStatus === 'Completed') {
      shiftType = 'completed';
    }
    const breach = getBreakStatus(row.startedOnActualUTC, row.shiftBreaks, row.endedOnActualUTC);
    let breachBg = '#d4edda';
    let breachText = '#155724';
    if (breach.severity === 'high') {
      breachBg = '#f8d7da';
      breachText = '#721c24';
    } else if (breach.severity === 'warning') {
      breachBg = '#fff3cd';
      breachText = '#856404';
    }
    const footer = (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            label={breach.tooltip}
            size="small"
            sx={{
              backgroundColor: breachBg,
              color: breachText,
              borderRadius: '12px',
              '& .MuiChip-label': { whiteSpace: 'normal' }
            }}
          />
        </Box>
        <Typography variant="caption" color="text.secondary">
          Scheduled: {formatDateToLongDate(row.scheduledStartUTC, true)}
        </Typography>
      </Box>
    );

    const actionNode = (
      <Tooltip title="Click to notify" arrow>
        <IconButton color="primary" aria-label="notify" size="small" onClick={(e) => { e.stopPropagation(); handleNotifyClick(row); }}>
          <NotificationsIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    );

    const shiftForCard = {
      ...row,
      userName: `${row?.user?.firstName ?? ''} ${row?.user?.lastName ?? ''}`.trim() || row.userEmail,
    };

    return (
      <MobileShiftCard key={row.id} shift={shiftForCard} shiftType={shiftType} footer={footer} actionNode={actionNode} />
    );
  };

  const iconContainerStyle = (isActive: boolean): React.CSSProperties => ({
    backgroundColor: isActive ? '#d3f9d8' : '#f1f1f1',
    borderRadius: '50%',
    padding: 6,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: 30,
    height: 30,
    whiteSpace: 'nowrap',
    color: isActive ? 'green' : 'grey',
    fontWeight: 500
  });

  const getFormSubmission = (submissionId: number, stateId: number) => {
    if (submissionId === 0) {
      return;
    }
    showBusyIndicator();

    const payload = {
      actionId: 904,
      stateId,
      queryParams: {
        id: submissionId.toString()
      }
    };

    FleetIoService.fleetIoPage(payload)
      .then((resp) => {
        setFormSubmissionData(resp);
        setFormDialogOpen(true);
        hideBusyIndicator();
      }).catch((err: any) => {
        hideBusyIndicator();
        showAlert(formatErrorMessage(JSON.parse(err.response)));
      });
  };

  const theme = useTheme();

  const renderFormDialog = () => {
    if (!formSubmissionData) return null;

    const getStatusColor = (result: any) => {
      if (!result) return 'default';
      if (result.label === 'Yes' || result.label === 'Pass' || result.label === 'Pass - Vehicle is roadworthy' || result.label === 'Pass - No significant damages or defects') return 'success';
      if (result.label === 'No' || result.label?.includes('Fail')) return 'error';
      return 'info';
    };

    const renderValue = (item: any) => {
      // Handle photo items
      if (item.result && (item.result?.full_file_url || item.result?.full_signature_file_url)) {
        return (
          <Box
            sx={{
              mt: 1,
              position: 'relative',
              '&:hover .zoom-overlay': {
                opacity: 1
              }
            }}
          >
            <Button
              component="div"
              onClick={() => window.open(item.result?.full_file_url || item.result?.full_signature_file_url, '_blank')}
              sx={{
                p: 0,
                width: '100%',
                height: 200,
                display: 'block',
                textAlign: 'left',
                '&:hover': {
                  backgroundColor: 'transparent'
                }
              }}
            >
              <img
                src={item.result.full_file_url || item.result?.full_signature_file_url}
                alt={item.inspection_item.label}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  borderRadius: '4px'
                }}
              />
              <Box
                className="zoom-overlay"
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(0,0,0,0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: 0,
                  transition: 'opacity 0.2s',
                  borderRadius: '4px'
                }}
              >
                <Typography color="white">Click to view full size</Typography>
              </Box>
            </Button>
          </Box>
        );
      }

      // Handle numeric items
      if (item.inspection_item.type === 'InspectionNumericItem') {
        return (
          <Typography variant="body1" sx={{ mt: 1, color: 'text.primary' }}>
            {item.result.value}°C
          </Typography>
        );
      }

      // Handle meter entry items
      if (item.inspection_item.type === 'InspectionMeterEntryItem') {
        return (
          <Typography variant="body1" sx={{ mt: 1, color: 'text.primary' }}>
            {item.meter_entry_value.toLocaleString()} km
          </Typography>
        );
      }

      // Handle dropdown items or pass/fail items
      return null;
      // (
      //   <Typography
      //     variant="body1"
      //     sx={{
      //       mt: 1,
      //       color: item.result?.label ? 'text.primary' : 'text.secondary'
      //     }}
      //   >
      //     {item.result?.label || item.result?.text || '—'}
      //   </Typography>
      // );
    };

    // Group items by their type
    const groupedItems = formSubmissionData.submitted_inspection_items.reduce((acc: any, item: any) => {
      const { type } = item.inspection_item;
      if (!acc[type]) acc[type] = [];
      acc[type].push(item);
      return acc;
    }, {});

    // Custom order for groups
    const groupOrder = [
      'InspectionMeterEntryItem',
      'InspectionNumericItem',
      'InspectionDropdownItem',
      'InspectionPassFailItem',
      'InspectionFreeTextItem',
      'InspectionPhotoItem',
      'InspectionSignatureItem'
    ];

    const getGroupTitle = (type: string) => {
      switch (type) {
        case 'InspectionMeterEntryItem': return 'Vehicle Readings';
        case 'InspectionDropdownItem': return 'Dropdown Fields';
        case 'InspectionNumericItem': return 'Temperature Readings';
        case 'InspectionPassFailItem': return 'Pass/Fail Items';
        case 'InspectionFreeTextItem': return 'Additional Information';
        case 'InspectionPhotoItem': return 'Photos';
        case 'InspectionSignatureItem': return 'Signatures';
        default: return type;
      }
    };

    return (
      <Dialog
        open={formDialogOpen}
        onClose={() => setFormDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
            <Box>
              <Typography variant="h6" sx={{ mb: 0.5 }}>
                {formSubmissionData.inspection_form.title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formSubmissionData.inspection_form.description}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
              <Stack spacing={0.5}>
                <Chip
                  label={`Submitted on ${moment(formSubmissionData.submitted_at).format('LLL')}`}
                  color="primary"
                  size="small"
                />
                {formSubmissionData.duration && (
                  <Chip
                    label={`Duration: ${formSubmissionData.duration}`}
                    size="small"
                    variant="outlined"
                  />
                )}
              </Stack>
              <Typography variant="caption" color="text.secondary">
                By {formSubmissionData.user}
              </Typography>
            </Box>
          </Box>
          {/* {formSubmissionData.starting_latitude && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" display="block" color="text.secondary">
                Started at: {formSubmissionData.starting_latitude}, {formSubmissionData.starting_longitude}
              </Typography>
              <Typography variant="caption" display="block" color="text.secondary">
                Submitted at: {formSubmissionData.submitted_latitude}, {formSubmissionData.submitted_longitude}
              </Typography>
            </Box>
          )} */}
        </DialogTitle>
        <DialogContent dividers>
          {groupOrder.map((groupType) => {
            if (!groupedItems[groupType]?.length) return null;

            return (
              <Box key={groupType} sx={{ mb: 3, '&:last-child': { mb: 0 } }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    mb: 2,
                    color: 'primary.main',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontWeight: 600
                  }}
                >
                  {getGroupTitle(groupType)}
                </Typography>
                <Grid container spacing={2}>
                  {groupedItems[groupType].map((item: any, index: number) => (
                    <Grid
                      item
                      xs={12}
                      md={groupType === 'InspectionPhotoItem' ? 6 : 12}
                      key={item.id}
                    >
                      <Box
                        sx={{
                          p: 2,
                          backgroundColor: theme.palette.background.default,
                          borderRadius: 1,
                          border: `1px solid ${theme.palette.divider}`,
                          transition: 'all 0.2s',
                          '&:hover': {
                            boxShadow: theme.shadows[1],
                            backgroundColor: theme.palette.background.paper
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 500, color: 'text.primary' }}>
                            {item.inspection_item.label}
                          </Typography>
                          {(item.result?.label || item.result?.text) && (
                            <Chip
                              label={item.result.label || item.result?.text || '—'}
                              size="small"
                              color={getStatusColor(item.result)}
                              sx={{ minWidth: 70 }}
                            />
                          )}
                        </Box>
                        {renderValue(item)}
                        {/* {item.latitude && item.longitude && (
                          <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                            Location: {item.latitude}, {item.longitude}
                          </Typography>
                        )} */}
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            );
          })}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setFormDialogOpen(false)}
            variant="outlined"
            color="primary"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const renderComplianceIcons = (row: any) => {
    const checkinResult = getCheckinStatus(row.checkins, row.scheduledStartActualUTC);
    const locationResult = getLocationStatus(row.checkins, row.jobsite?.address?.latitude, row.jobsite?.address?.longitude, row.scheduledStartActualUTC);
    const prestartResult = getPrestartStatus(row.checkins);
    const breakResult = getBreakStatus(row.startedOnActualUTC, row.shiftBreaks, row.endedOnActualUTC);

    return (
      <div style={{ display: 'flex', gap: 6 }}>
        <Tooltip title={checkinResult.tooltip} arrow>
          <div style={getIconContainerStyle(checkinResult.status)}>
            <PersonAddIcon style={{ ...iconStyle, color: getIconColor(checkinResult.status) }} />
          </div>
        </Tooltip>
        <Tooltip title={locationResult.tooltip} arrow>
          <div style={getIconContainerStyle(locationResult.status)}>
            <LocationOnIcon style={{ ...iconStyle, color: getIconColor(locationResult.status) }} />
          </div>
        </Tooltip>
        <Tooltip title={prestartResult.tooltip} arrow>
          <div style={getIconContainerStyle(prestartResult.status)}>
            <PlaylistAddCheckIcon style={{ ...iconStyle, color: getIconColor(prestartResult.status) }} />
          </div>
        </Tooltip>
        <Tooltip title={breakResult.tooltip} arrow>
          <div style={getIconContainerStyle(breakResult.status)}>
            <HotelIcon style={{ ...iconStyle, color: getIconColor(breakResult.status) }} />
          </div>
        </Tooltip>
      </div>
    );
  };

  const renderFleetIoComplianceIcons = (row: any) => {
    const shiftId = row.id;

    const precheck = fleetIoPrecheckCompleteShifts.find((x) => x.shiftId === shiftId);
    const fridgeInspection = fleetIoFridgeInspectionCompleteShifts.find((x) => x.shiftId === shiftId);

    const precheckCompleted = Boolean(precheck);
    const fridgeInspectionCompleted = Boolean(fridgeInspection);

    return (
      <div style={{ display: 'flex', gap: 6 }}>
        {isFleetIoLoading ? (
          <CircularProgress size={20} />
        ) : (
          <>
            <Tooltip title={precheckCompleted ? 'FleetIo Precheck Completed - Compliant' : 'No precheck submission recorded in 2 hours before scheduled start'}>
              <div
                role="button"
                tabIndex={0}
                style={iconContainerStyle(precheckCompleted)}
                onClick={() =>
                  getFormSubmission(precheck?.submissionId || 0, precheck?.state || 0)
                }
                onKeyPress={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    getFormSubmission(precheck?.submissionId || 0, precheck?.state || 0);
                  }
                }}
              >
                <AssignmentTurnedInIcon
                  style={{
                    ...iconStyle,
                    color: precheckCompleted ? 'green' : 'goldenrod'
                  }}
                />
              </div>
            </Tooltip>
            <Tooltip title={fridgeInspectionCompleted ? 'FleetIo Fridge Inspection Completed - Compliant' : 'No fridge inspection recorded in 2 hours before scheduled start for assigned vehicle'}>
              <div
                role="button"
                tabIndex={0}
                style={iconContainerStyle(fridgeInspectionCompleted)}
                onClick={() =>
                  getFormSubmission(fridgeInspection?.submissionId || 0, fridgeInspection?.state || 0)
                }
                onKeyPress={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    getFormSubmission(fridgeInspection?.submissionId || 0, fridgeInspection?.state || 0);
                  }
                }}
              >
                <AcUnitIcon
                  style={{
                    ...iconStyle,
                    color: fridgeInspectionCompleted ? 'green' : 'goldenrod'
                  }}
                />
              </div>
            </Tooltip>
          </>
        )}

        {/* WooliesGo Custom Icon */}
        <Tooltip title={row?.checkinPhoto !== '' ? 'WooliesGo checkin - Click to view image - Compliant' : 'WooliesGo checkin pending - Non-Compliant'}>
          <div
            role="button"
            tabIndex={0}
            style={iconContainerStyle(row?.checkinPhoto !== '')}
            onClick={() => handleOpenImage(row?.checkinPhoto)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleOpenImage(row?.checkinPhoto);
              }
            }}
          >
            GO
          </div>
        </Tooltip>
      </div>
    );
  };

  const routeColumns = [
    {
      field: 'scheduledStartDateUTC',
      headerName: 'Date',
      flex: 1,
      minWidth: 100,
      type: 'date',
      filterable: false,
      renderHeader: () => (
        <Tooltip title="Scheduled date of the shift">
          <span style={{ fontWeight: '600' }}>Date</span>
        </Tooltip>
      ),
      renderCell: (params: any) => {
        const { value, row } = params;
        return (
          <Tooltip title={formatDateToCalendarTime(row.scheduledStartUTC, true) || ''}>
            <span>
              {formatDateToOnlyDate(row.scheduledStartUTC, true)}
            </span>
          </Tooltip>
        );
      }
    },
    {
      field: 'userName',
      headerName: 'Driver',
      width: 150,
      renderCell: ({ row }: any) => (
        <Tooltip title={row.userName || row.userEmail || ''}>
          <span>{row.userName || row.userEmail}</span>
        </Tooltip>
      )
    },
    {
      field: 'amOrPm',
      headerName: 'Shift',
      minWidth: 10,
      filterable: true,
      renderCell: (params: any) => {
        const { value, row } = params;
        return (
          <Tooltip title={value}>
            <span>{value || '-'}</span>
          </Tooltip>
        );
      }
    },
    {
      field: 'compliance',
      headerName: 'TruckSight Compliance',
      flex: 1,
      minWidth: 150,
      renderCell: ({ row }: any) => renderComplianceIcons(row)
    },
    {
      field: 'shiftDate',
      headerName: 'FleetIo Compliance',
      flex: 1,
      minWidth: 120,
      renderCell: ({ row }: any) => renderFleetIoComplianceIcons(row)
    },
    {
      field: 'shift',
      headerName: 'Shift',
      width: 100,
      hide: true,
      renderCell: ({ row }: any) => (
        <Tooltip title={row?.extCompany?.name || ''}>
          <span>{row?.extCompany?.name || '-'}</span>
        </Tooltip>
      )
    },
    {
      field: 'amOrPm',
      headerName: 'Shift',
      width: 100,
      renderCell: ({ row }: any) => (
        <Tooltip title={row.amOrPm || ''}>
          <span>{row.amOrPm || '-'}</span>
        </Tooltip>
      )
    },
    {
      field: 'scheduledActual',
      headerName: 'Dock',
      width: 100,
      renderCell: ({ row }: any) => (
        <Tooltip title={row.dock || ''}>
          <span>{row.dock || '-'}</span>
        </Tooltip>
      )
    },
    {
      field: 'driveHours',
      headerName: 'Status',
      width: 100,
      renderCell: ({ row }: any) => (
        <Tooltip title={row.shiftStatus || ''}>
          <span>{row.shiftStatus || '-'}</span>
        </Tooltip>
      )
    },
    {
      field: 'timeToBreach',
      headerName: 'Rego',
      width: 100,
      renderCell: ({ row }: any) => (
        <Tooltip title={row.vehicle?.registrationNumber || row.vehicleRego || ''}>
          <span>{row.vehicle?.registrationNumber || row.vehicleRego || '—'}</span>
        </Tooltip>
      )
    },
    {
      field: 'scheduledStartUTC',
      headerName: 'Start',
      flex: 1,
      minWidth: 100,
      type: 'date',
      filterable: false,
      renderHeader: () => (
        <Tooltip title="Scheduled start time of the shift">
          <span style={{ fontWeight: '600' }}>Start</span>
        </Tooltip>
      ),
      renderCell: (params: any) => {
        const { value } = params;
        return (
          <Tooltip title={formatDateToCalendarTime(value, true) || ''}>
            <span>
              {formatDateToOnlyTime(value, true)}
            </span>
          </Tooltip>
        );
      }
    },
    {
      field: 'scheduledEndUTC',
      headerName: 'End',
      flex: 1,
      minWidth: 100,
      type: 'date',
      filterable: false,
      renderHeader: () => (
        <Tooltip title="Scheduled end time of the shift">
          <span style={{ fontWeight: '600' }}>End</span>
        </Tooltip>
      ),
      renderCell: (params: any) => {
        const { value } = params;
        return (
          <Tooltip title={formatDateToCalendarTime(value, true) || ''}>
            <span>
              {value && formatDateToOnlyTime(value, true)}
            </span>
          </Tooltip>
        );
      }
    },
    {
      field: 'startedOnUTC',
      headerName: 'Actual Start',
      flex: 1,
      minWidth: 100,
      type: 'date',
      filterable: false,
      renderHeader: () => (
        <Tooltip title="Actual time when the shift started">
          <span style={{ fontWeight: '600' }}>Actual Start</span>
        </Tooltip>
      ),
      renderCell: (params: any) => {
        const { value } = params;
        return (
          <Tooltip title={formatDateToCalendarTime(value, true) || ''}>
            <span>
              {formatDateToOnlyTime(value, true)}
            </span>
          </Tooltip>
        );
      }
    },
    {
      field: 'endedOnUTC',
      headerName: 'Actual End',
      flex: 1,
      minWidth: 100,
      type: 'date',
      filterable: false,
      renderHeader: () => (
        <Tooltip title="Actual time when the shift ended">
          <span style={{ fontWeight: '600' }}>Actual End</span>
        </Tooltip>
      ),
      renderCell: (params: any) => {
        const { value } = params;
        return (
          <Tooltip title={formatDateToCalendarTime(value, true) || ''}>
            <span>
              {value && formatDateToOnlyTime(value, true)}
            </span>
          </Tooltip>
        );
      }
    },
    {
      field: 'userExtDriverId',
      headerName: 'CSA ID',
      width: 120,
      filterable: false
    },
    {
      field: 'scheduledDrops',
      headerName: 'Drops',
      width: 120
    },
    {
      field: 'routePerformanceWeekEnding',
      headerName: 'Route Performance Week Ending',
      width: 300,
      renderCell: ({ value }: any) => <span>{value ?? '—'}</span>,
    },
    {
      field: 'routePerformanceOnTimeOrders',
      headerName: 'Route Performance On Time Orders',
      width: 300,
      renderCell: ({ value }: any) => <span>{value ?? '—'}</span>,
    },
    {
      field: 'routePerformanceOrders',
      headerName: 'Route Performance Orders',
      width: 300,
      renderCell: ({ value }: any) => <span>{value ?? '—'}</span>,
    },
    {
      field: 'routePerformancePlannedReload',
      headerName: 'Route Performance Planned Reload',
      width: 300,
      renderCell: ({ value }: any) => <span>{value ?? '—'}</span>,
    },
    {
      field: 'routePerformanceOtdPercentage',
      headerName: 'Route Performance OTD Percentage',
      width: 300,
      renderCell: ({ value }: any) => <span>{value ?? '—'}</span>,
    },
    {
      field: 'routePerformanceOveragePercentage',
      headerName: 'Route Performance Overage Percentage',
      width: 300,
      renderCell: ({ value }: any) => <span>{value ?? '—'}</span>,
    },
    {
      field: 'routePerformanceLateArrivalMinsPerRoute',
      headerName: 'Route Performance Late Arrival Mins Per Route',
      width: 300,
      renderCell: ({ value }: any) => <span>{value ?? '—'}</span>,
    },
    {
      field: 'routePerformanceDwellPerRoute',
      headerName: 'Route Performance Dwell Per Route',
      width: 300,
      renderCell: ({ value }: any) => <span>{value ?? '—'}</span>,
    },
    {
      field: 'routePerformanceUnloadingPerRoute',
      headerName: 'Route Performance Unloading Per Route',
      width: 300,
      renderCell: ({ value }: any) => <span>{value ?? '—'}</span>,
    },
    {
      field: 'routePerformanceWGOIdlePerRoute',
      headerName: 'Route Performance WGO Idle Per Route',
      width: 300,
      renderCell: ({ value }: any) => <span>{value ?? '—'}</span>,
    },
    {
      field: 'routePerformanceMicroliseCustomerIdlePerRoute',
      headerName: 'Route Performance Microlise Customer Idle Per Route',
      width: 300,
      renderCell: ({ value }: any) => <span>{value ?? '—'}</span>,
    },
    {
      field: 'routePerformanceMicroliseEnrouteIdlePerRoute',
      headerName: 'Route Performance Microlise Enroute Idle Per Route',
      width: 300,
      renderCell: ({ value }: any) => <span>{value ?? '—'}</span>,
    },
    {
      field: 'routePerformanceMicroliseIgnitionOffOutsideGeofence',
      headerName: 'Route Performance Microlise Ignition Off Outside Geofence',
      width: 350,
      renderCell: ({ value }: any) => <span>{value ?? '—'}</span>,
    },
    {
      field: 'routePerformanceTotalIdleTimePerRoute',
      headerName: 'Route Performance Total Idle Time Per Route',
      width: 300,
      renderCell: ({ value }: any) => <span>{value ?? '—'}</span>,
    },
    {
      field: 'routePerformanceCheckoutOutsideGeofencePerRoute',
      headerName: 'Route Performance Checkout Outside Geofence Per Route',
      width: 350,
      renderCell: ({ value }: any) => <span>{value ?? '—'}</span>,
    },
    {
      field: 'routePerformanceServiceAndUnknownOveragePerRoute',
      headerName: 'Route Performance Service And Unknown Overage Per Route',
      width: 350,
      renderCell: ({ value }: any) => <span>{value ?? '—'}</span>,
    },
    {
      field: 'routePerformanceDriveAndUnknownOveragePerRoute',
      headerName: 'Route Performance Drive And Unknown Overage Per Route',
      width: 350,
      renderCell: ({ value }: any) => <span>{value ?? '—'}</span>,
    }
  ];

  const renderEachRouteCard = (row: any) => {
    let shiftType: 'ongoing' | 'completed' | 'scheduled' = 'scheduled';
    if (row.shiftStatus === 'Ongoing') {
      shiftType = 'ongoing';
    } else if (row.shiftStatus === 'Completed') {
      shiftType = 'completed';
    }
    const footer = (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {renderComplianceIcons(row)}
          </Box>
          <Chip label={row.shiftStatus || '-'} size="small" variant="outlined" />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="caption" color="text.secondary">Dock: {row.dock || '-'}</Typography>
          <Typography variant="caption" color="text.secondary">Vehicle: {row.vehicle?.registrationNumber || row.vehicleRego || '—'}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="caption" color="text.secondary">Start: {formatDateToOnlyTime(row.scheduledStartUTC, true)}</Typography>
          <Typography variant="caption" color="text.secondary">End: {row.scheduledEndUTC && formatDateToOnlyTime(row.scheduledEndUTC, true)}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="caption" color="text.secondary">Timesheet: {row.timesheetStatus || 'No'}</Typography>
          <Typography variant="caption" color="text.secondary">Drops: {row.scheduledDrops || '0'}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" color="text.secondary">FleetIo:</Typography>
          {renderFleetIoComplianceIcons(row)}
        </Box>
      </Box>
    );

    const shiftForCard = row;
    return (
      <MobileShiftCard key={row.id} shift={shiftForCard} shiftType={shiftType} footer={footer} />
    );
  };

  const onSearchTextChange = (event: any) => {
    filterByText(event?.target?.value);
  };

  const onRowClick = ({ id, extCompany }: any) => {
    window.open(`/app/shift/list/${id}`, '_blank');
  };

  const handleSendNotification = () => {
    showBusyIndicator();
    const shiftWarningReq = {
      message: notificationMessage,
      shiftId: selectedRow?.id
    };

    ShiftsService.warnDriver(shiftWarningReq)
      .then(() => {
        showAlert({ message: 'Notification sent successfully', severity: 'success' });
        setNotifyDialogOpen(false);
        setSelectedRow(null);
        setNotificationMessage('');
      })
      .catch((err: any) => {
        showAlert(formatErrorMessage(JSON.parse(err.response)));
      }).then(() => {
        hideBusyIndicator();
      });
  };

  return (
    <>
      <Modal open={open} onClose={handleClose}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100vh',
          }}
          onClick={handleClose}
        >
          <img src={selectedImage} alt="Check-in Full View" style={{ maxWidth: '90%', maxHeight: '90%', borderRadius: 8 }} />
        </Box>
      </Modal>
      <Box
        sx={{
          mt: 2,
          display: 'flex',
          flexDirection: { xs: 'column-reverse', sm: 'row' },
          alignItems: 'center',
          gap: 2,
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <GlobalSearch
          searchedText={searchedText}
          onSearchTextChange={onSearchTextChange}
          showFilterByColumn={false}
          width="250px"
        />

        {(() => {
          // Shared data for counts section
          const countsData = [
            {
              label: 'Total',
              value: filteredVisibleRows.length,
              color: '#7E57C2',
              icon: <NumbersIcon style={{ color: '#7E57C2' }} fontSize="small" />
            },
            {
              label: 'Scheduled',
              value: filteredVisibleRows.filter((x) => x.shiftStatus === 'Scheduled').length,
              color: '#FF7043',
              icon: <ScheduleIcon style={{ color: '#FF7043' }} fontSize="small" />
            },
            {
              label: 'Accepted',
              value: filteredVisibleRows.filter((x) => x.userHasAccepted).length,
              color: '#26A69A',
              icon: <CheckCircleOutlineIcon style={{ color: '#26A69A' }} fontSize="small" />
            },
            {
              label: 'Ongoing',
              value: filteredVisibleRows.filter((x) => x.shiftStatus === 'Ongoing').length,
              color: '#FFCA28',
              icon: <HourglassBottomIcon style={{ color: '#FFCA28' }} fontSize="small" />
            },
            {
              label: 'Completed',
              value: filteredVisibleRows.filter((x) => x.shiftStatus === 'Completed').length,
              color: '#66BB6A',
              icon: <TaskAltIcon style={{ color: '#66BB6A' }} fontSize="small" />
            },
            {
              label: 'Timesheets',
              value: filteredVisibleRows.filter((x) => x.timesheetStatus === 'Yes').length,
              color: '#5C6BC0',
              icon: <DescriptionIcon style={{ color: '#5C6BC0' }} fontSize="small" />
            },
            {
              label: 'AM',
              value: filteredVisibleRows.filter((x) => x.amOrPm === 'AM').length,
              color: '#42A5F5',
              icon: <ScheduleIcon style={{ color: '#42A5F5' }} fontSize="small" />
            },
            {
              label: 'PM',
              value: filteredVisibleRows.filter((x) => x.amOrPm === 'PM').length,
              color: '#EF5350',
              icon: <ScheduleIcon style={{ color: '#EF5350' }} fontSize="small" />
            }
          ];

          return (
            <>
              {/* Desktop: keep existing chips layout */}
              <Hidden mdDown>
                <Box
                  sx={{
                    mt: 3,
                    mb: 3,
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 1.5,
                    position: 'relative',
                  }}
                >
                  {countsData.map((item, index) => (
                    <Chip
                      key={index}
                      clickable
                      onClick={() => setSelectedSummary(item.label)}
                      onDelete={selectedSummary === item.label && item.label !== 'Total' ? () => setSelectedSummary('') : undefined}
                      deleteIcon={selectedSummary === item.label && item.label !== 'Total' ? <CancelIcon fontSize="small" /> : undefined}
                      label={(
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {item.icon}
                          <Box>
                            <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
                              {item.value}
                            </Typography>
                            <Typography
                              variant="body2"
                              component="span"
                              sx={{
                                ml: 0.5,
                                fontWeight: selectedSummary === item.label ? 700 : 400,
                                cursor: 'pointer',
                              }}
                            >
                              {item.label}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                      sx={{
                        height: 36,
                        borderRadius: 18,
                        bgcolor: `${item.color}4D`,
                        border: `1px solid ${item.color}80`,
                        '& .MuiChip-label': {
                          px: 1.5,
                          py: 0.5
                        },
                        '&:hover': {
                          bgcolor: `${item.color}12`,
                          transform: 'translateY(-1px)'
                        },
                        transition: 'all 0.2s ease',
                        boxShadow: selectedSummary === item.label ? `0 0 0 2px ${item.color}80` : undefined,
                        fontWeight: selectedSummary === item.label ? 700 : 400,
                      }}
                      variant={selectedSummary === item.label ? 'filled' : 'outlined'}
                    />
                  ))}
                </Box>
              </Hidden>

              {/* Mobile: show card-style stats */}
              <Hidden mdUp>
                <Grid container spacing={1.5} sx={{ mt: 2, mb: 2, width: '100%' }}>
                  {countsData.map((item) => {
                    const isSelected = selectedSummary === item.label;
                    const alphaBg = `${item.color}14`; // ~8% tint
                    const alphaBorder = `${item.color}40`; // ~25% border
                    return (
                      <Grid item xs={6} key={item.label}>
                        <Card
                          elevation={isSelected ? 3 : 0}
                          onClick={() => setSelectedSummary(
                            isSelected && item.label !== 'Total' ? '' : item.label
                          )}
                          sx={{
                            cursor: 'pointer',
                            borderRadius: 2,
                            border: `1px solid ${alphaBorder}`,
                            background: alphaBg,
                            transition: 'all .2s ease',
                            '&:active': { transform: 'scale(0.995)' },
                            boxShadow: isSelected ? `0 0 0 2px ${alphaBorder}` : 'none'
                          }}
                        >
                          <CardContent sx={{ p: '10px !important' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box
                                sx={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: '50%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  backgroundColor: `${item.color}26`
                                }}
                              >
                                {item.icon}
                              </Box>
                              <Box sx={{ ml: 0.5 }}>
                                <Typography variant="subtitle1" sx={{ lineHeight: 1.1, fontWeight: 700, color: '#161725' }}>
                                  {item.value}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                  {item.label}
                                </Typography>
                              </Box>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              </Hidden>
            </>
          );
        })()}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: { xs: '100%', sm: 'auto' } }}>
          {StatesDropdown}
        </Box>
      </Box>
      {renderFormDialog()}
      <Dialog
        open={notifyDialogOpen}
        onClose={() => setNotifyDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Send Warning to {selectedRow?.user?.firstName} {selectedRow?.user?.lastName}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            multiline
            rows={6}
            value={notificationMessage}
            onChange={(e) => setNotificationMessage(e.target.value)}
            fullWidth
            variant="outlined"
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotifyDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleSendNotification}
            color="primary"
            variant="contained"
            disabled={!notificationMessage.trim()}
          >
            Send
          </Button>
        </DialogActions>
      </Dialog>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          mb: 2,
          marginTop: 2,
          alignItems: 'center',
          justifyContent: 'flex-start',
        }}
      >
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Chip
            icon={<ErrorIcon style={{ color: '#d32f2f' }} fontSize="small" />}
            label={(
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
                  {filteredVisibleRows.filter((x) => getBreakStatus(x.startedOnActualUTC, x.shiftBreaks, x.endedOnActualUTC).severity === 'high' || getBreakStatus(x.startedOnActualUTC, x.shiftBreaks, x.endedOnActualUTC).status === 'red').length}
                </Typography>
                <Typography
                  variant="body2"
                  component="span"
                  sx={{
                    ml: 0.5,
                    fontWeight: selectedSummary === 'Breached' ? 700 : 400,
                    cursor: 'pointer',
                  }}
                >
                  Breached
                </Typography>
              </Box>
            )}
            clickable
            onClick={() => setSelectedSummary('Breached')}
            onDelete={selectedSummary === 'Breached' ? () => setSelectedSummary('') : undefined}
            deleteIcon={selectedSummary === 'Breached' ? <CancelIcon fontSize="small" /> : undefined}
            sx={{
              height: 36,
              borderRadius: 18,
              border: 'none',
              transition: 'all 0.2s ease',
              fontWeight: selectedSummary === 'Breached' ? 700 : 400,
            }}
            variant={selectedSummary === 'Breached' ? 'filled' : 'outlined'}
          />
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Chip
            icon={<ReportProblemIcon style={{ color: '#ffa000' }} fontSize="small" />}
            label={(
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
                  {filteredVisibleRows.filter((x) => getBreakStatus(x.startedOnActualUTC, x.shiftBreaks, x.endedOnActualUTC).severity === 'warning').length}
                </Typography>
                <Typography
                  variant="body2"
                  component="span"
                  sx={{
                    ml: 0.5,
                    fontWeight: selectedSummary === 'High Risk' ? 700 : 400,
                    cursor: 'pointer',
                  }}
                >
                  At Risk of Breach
                </Typography>
              </Box>
            )}
            clickable
            onClick={() => setSelectedSummary('High Risk')}
            onDelete={selectedSummary === 'High Risk' ? () => setSelectedSummary('') : undefined}
            deleteIcon={selectedSummary === 'High Risk' ? <CancelIcon fontSize="small" /> : undefined}
            sx={{
              height: 36,
              borderRadius: 18,
              border: 'none',
              transition: 'all 0.2s ease',
              fontWeight: selectedSummary === 'High Risk' ? 700 : 400,
            }}
            variant={selectedSummary === 'High Risk' ? 'filled' : 'outlined'}
          />
        </Stack>
      </Box>

      {/* Filter rows by selected summary chip, including AM/PM, Breached, and High Risk */}
      {(() => {
        let filteredRows = filteredVisibleRows.slice().reverse();
        if (selectedSummary === 'Scheduled') {
          filteredRows = filteredVisibleRows.slice().reverse().filter((x) => x.shiftStatus === 'Scheduled');
        } else if (selectedSummary === 'Accepted') {
          filteredRows = filteredVisibleRows.slice().reverse().filter((x) => x.userHasAccepted);
        } else if (selectedSummary === 'Ongoing') {
          filteredRows = filteredVisibleRows.slice().reverse().filter((x) => x.shiftStatus === 'Ongoing');
        } else if (selectedSummary === 'Completed') {
          filteredRows = filteredVisibleRows.slice().reverse().filter((x) => x.shiftStatus === 'Completed');
        } else if (selectedSummary === 'Timesheets') {
          filteredRows = filteredVisibleRows.slice().reverse().filter((x) => x.timesheetStatus === 'Yes');
        } else if (selectedSummary === 'AM') {
          filteredRows = filteredVisibleRows.slice().reverse().filter((x) => x.amOrPm === 'AM');
        } else if (selectedSummary === 'PM') {
          filteredRows = filteredVisibleRows.slice().reverse().filter((x) => x.amOrPm === 'PM');
        } else if (selectedSummary === 'Breached') {
          filteredRows = filteredVisibleRows.slice().reverse().filter((x) => getBreakStatus(x.startedOnActualUTC, x.shiftBreaks, x.endedOnActualUTC).severity === 'high' || getBreakStatus(x.startedOnActualUTC, x.shiftBreaks, x.endedOnActualUTC).status === 'red');
        } else if (selectedSummary === 'High Risk') {
          filteredRows = filteredVisibleRows.slice().reverse().filter((x) => getBreakStatus(x.startedOnActualUTC, x.shiftBreaks, x.endedOnActualUTC).severity === 'warning');
        }
        // For the breaches table, always show only breaches (red or high severity or warning)
        const breaches = filteredRows.filter(
          (x) => getBreakStatus(x.startedOnActualUTC, x.shiftBreaks, x.endedOnActualUTC).status === 'red'
            || getBreakStatus(x.startedOnActualUTC, x.shiftBreaks, x.endedOnActualUTC).severity === 'high'
            || getBreakStatus(x.startedOnActualUTC, x.shiftBreaks, x.endedOnActualUTC).severity === 'warning'
        );
        if (breaches.length === 0) {
          return (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: '#f5f5f5',
                padding: 2,
                borderRadius: 1,
                textAlign: 'center',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  fontStyle: 'italic',
                }}
              >
                No shift breaches.
              </Typography>
            </Box>
          );
        }
        return (
          <>
            <Hidden mdDown>
              <Table
                rows={breaches}
                columns={breachColumns}
                onRowDoubleClick={({ row }) => onRowClick(row)}
                page={page}
                pageSize={5}
                totalRows={breaches.length}
                sortModel={sortModel}
                onPageChange={(newPage) => setPage(newPage)}
                onSortModelChange={(model) => setSortModel(model)}
              />
            </Hidden>
            <Hidden mdUp>
              <MobileDataList
                rows={breaches}
                renderEachRow={renderEachShiftCard}
                totalRows={breaches.length}
                page={page}
                onPageChange={(newPage: any) => setPage(newPage)}
              />
            </Hidden>
          </>
        );
      })()}

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 4, mb: 2 }}>
        <Typography variant="h3" gutterBottom sx={{ color: '#161725', mr: 2 }}>
          Today&apos;s Routes
        </Typography>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel id="rows-per-page-label">Rows per page</InputLabel>
          <Select
            labelId="rows-per-page-label"
            id="rows-per-page-select"
            value={rowsPerPage}
            label="Rows per page"
            onChange={(e) => setRowsPerPage(Number(e.target.value))}
          >
            <MenuItem value={5}>5</MenuItem>
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={20}>20</MenuItem>
            <MenuItem value={30}>30</MenuItem>
            <MenuItem value={50}>50</MenuItem>
            <MenuItem value={100}>100</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Filter routes by selected summary chip */}
      {(() => {
        let filteredRows = filteredVisibleRows.slice().reverse();
        if (selectedSummary === 'Scheduled') {
          filteredRows = filteredVisibleRows.slice().reverse().filter((x) => x.shiftStatus === 'Scheduled');
        } else if (selectedSummary === 'Accepted') {
          filteredRows = filteredVisibleRows.slice().reverse().filter((x) => x.userHasAccepted);
        } else if (selectedSummary === 'Ongoing') {
          filteredRows = filteredVisibleRows.slice().reverse().filter((x) => x.shiftStatus === 'Ongoing');
        } else if (selectedSummary === 'Completed') {
          filteredRows = filteredVisibleRows.slice().reverse().filter((x) => x.shiftStatus === 'Completed');
        } else if (selectedSummary === 'Timesheets') {
          filteredRows = filteredVisibleRows.slice().reverse().filter((x) => x.timesheetStatus === 'Yes');
        } else if (selectedSummary === 'AM') {
          filteredRows = filteredVisibleRows.slice().reverse().filter((x) => x.amOrPm === 'AM');
        } else if (selectedSummary === 'PM') {
          filteredRows = filteredVisibleRows.slice().reverse().filter((x) => x.amOrPm === 'PM');
        }
        return (
          <>
            <Hidden mdDown>
              <Table
                rows={filteredRows}
                onRowDoubleClick={({ row }) => onRowClick(row)}
                columns={routeColumns}
                page={page}
                pageSize={rowsPerPage}
                totalRows={filteredRows.length}
                sortModel={sortModel}
                onPageChange={(newPage) => setPage(newPage)}
                onSortModelChange={(model) => setSortModel(model)}
              />
            </Hidden>
            <Hidden mdUp>
              <MobileDataList
                rows={filteredRows}
                renderEachRow={renderEachRouteCard}
                totalRows={filteredRows.length}
                page={page}
                onPageChange={(newPage: any) => setPage(newPage)}
              />
            </Hidden>
          </>
        );
      })()}
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  visibleRows: state.shift?.dashboard?.filteredRows || [],
  startDate: state.shift?.startDate || '',
  endDate: state.shift?.endDate || '',
  companyId: state.profile?.company?.id,
  searchedText: state.shift?.dashboard?.searchedText,
  filterByCol: state.shift?.dashboard?.filterByCol
});

const mapDispatchToProps = (dispatch: any) => ({
  filterByText: (text: string) => dispatch(searchByTextDashboard(text)),
  fetchAllRows: (start: string, end: string) => dispatch(refreshDataWithDateRange(start, end)),
  showBusyIndicator: () => dispatch(show()),
  hideBusyIndicator: () => dispatch(hide()),
  showAlert: (message: any) => dispatch(showGlobalAlert(message)),
});

export default connect(mapStateToProps, mapDispatchToProps)(DashboardToday);
